/* Redux */
import { AnyAction, Dispatch } from 'redux';
import { tipsActions } from '../../../store/tips/actions';
import { premiumInfoActions } from '../../../store/premium/actions';

/* Interfaces */
import { Price } from './Price';
import { Payload } from './Payload';
import { Shop } from '../../Shops/shared/interfaces';
import { AnalyticsClient } from '../../../analytics/Client';
import { StoreType, StoreTypesReversed } from './store_types';
import { TipProduct } from '../../Shops/Tip/interfaces/TipProduct';
import {
  PaymentError,
  PaymentProcessor,
  WithPayload,
} from './PaymentProcessor';
import { PremiumProduct } from '../../Shops/Premium/interfaces/PremiumProduct';
import { PurchaseStatus } from '../../Shops/shared/__providers__/PurchaseStatusProvider';

/* Services */
import {
  requestPremiumPaymentIntent,
  requestPremiumPaymentVerification,
} from '../../../api/payments/Paypal/premiumPayments';
import {
  requestTipPaymentIntent,
  requestTipPaymentVerification,
} from '../../../api/payments/Paypal/tipPayments';

/* Toast */
import { toast } from 'react-toastify';

/* Translations */
import i18n from '../../../i18n';

export class PaypalPaymentProcessor
  extends PaymentProcessor
  implements WithPayload
{
  protected purchaseID: string | null = null;
  public payload: Payload;

  constructor(
    payload: Payload,
    dispatcher: Dispatch<AnyAction>,
    loader: (load: boolean) => void,
    analyticsClient: AnalyticsClient,
    setPurchaseStatus: (status: PurchaseStatus) => void,
  ) {
    super(dispatcher, loader, analyticsClient, setPurchaseStatus);
    this.payload = payload;
  }

  async processPayment(store: StoreType): Promise<void> {
    this.loader(true);

    try {
      switch (store) {
        case StoreTypesReversed.Premium:
          const {
            premium_plan_purchase: { order_id: premiumOrderID, id: premiumID },
          } = await requestPremiumPaymentIntent(this.payload);

          this.purchaseID = premiumID;

          return premiumOrderID;

        case StoreTypesReversed.TipPurchase:
          const {
            purchase: {
              payment_provider: { client_secret: tipOrderID },
              id: tipID,
            },
          } = await requestTipPaymentIntent(this.payload);

          this.purchaseID = tipID;

          return tipOrderID;
      }
    } catch (error: unknown) {
      this.handleError(error as PaymentError);
    }
  }

  public async finishPayment(store: StoreType, product: Shop): Promise<any> {
    if (this.purchaseID) {
      const tipsterInfo = product.tipsterInfo;

      switch (store) {
        case StoreTypesReversed.Premium:
          requestPremiumPaymentVerification(this.purchaseID)
            .then((status: unknown) => {
              if (status === 200) {
                const premiumProduct =
                  product.selectedProduct as PremiumProduct;

                this.analyticsClient.firebase.log('purchase', [
                  {
                    transaction_id: this.purchaseID || '',
                    store: 'Paypal',
                    type: 'premium',
                    currency: premiumProduct.priceWithDiscount.currency,
                    value: premiumProduct.priceWithDiscount
                      .buildPrice()
                      .toNumber(),
                    items: [
                      {
                        item_id: premiumProduct.plan.pricing_tier_code,
                        item_name: `Premium Tier ${premiumProduct.getPricingTierCodeNumber()}`,
                        item_list_name: 'Premium',
                      },
                    ],
                  },
                ]);

                toast.success(`${i18n.t('translation:payment_success')}`);
                this.setPurchaseStatus(PurchaseStatus.COMPLETED);
                this.dispatcher(
                  premiumInfoActions.publicPremiumInfoRequestAction(
                    tipsterInfo.id,
                  ),
                );
              }
            })
            .catch((error: unknown) => {
              this.handleError(error as PaymentError);
            })
            .finally(() => {
              this.loader(false);
            });
          break;

        case StoreTypesReversed.TipPurchase:
          requestTipPaymentVerification(this.purchaseID)
            .then((status: any) => {
              if (status.tip.purchased) {
                const tipProduct = product.selectedProduct as TipProduct;

                this.analyticsClient.firebase.log('purchase', [
                  {
                    transaction_id: this.purchaseID || '',
                    store: 'Paypal',
                    type: 'tip_coupon',
                    currency: tipProduct.tip.web_price.currency,
                    value: new Price(
                      tipProduct.tip.web_price.amount,
                      tipProduct.tip.web_price.currency,
                      tipProduct.tip.web_price.decimals,
                    )
                      .buildPrice()
                      .toNumber(),
                    items: [
                      {
                        item_id: tipProduct.tip.pricing_tier_code,
                        item_name: tipProduct.tip.pricing_tier_code,
                        item_list_name: 'Tip',
                      },
                    ],
                  },
                ]);

                toast.success(`${i18n.t('translation:payment_success')}`);
                this.setPurchaseStatus(PurchaseStatus.COMPLETED);
                this.dispatcher(
                  tipsActions.tipProductRequestAction(tipProduct.tip.id),
                );
              }
            })
            .catch((error: unknown) => {
              this.handleError(error as PaymentError);
            })
            .finally(() => {
              this.loader(false);
            });
          break;
      }
    }
  }

  public handleError(error: PaymentError) {
    toast.error(error.detail || error.message);

    this.setPurchaseStatus(PurchaseStatus.FAILED);
    this.loader(false);
  }
}
